import { useState, useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import { canAccessAdminRoutes } from './lib/adminRoles'
import { shouldShowBootSplash, shouldShowSignup, shouldShowRestoreGate } from './lib/authResume'
import { applyPendingUpdateIfSafe } from './lib/swController'
import BottomNav from './components/BottomNav'
import ScrollToTop from './components/ScrollToTop'
import NotificationSoftAsk from './components/NotificationSoftAsk'
import SplashScreen from './components/SplashScreen'
import InstallPrompt from './components/InstallPrompt'
import HomePage from './pages/HomePage'
import PlayPage from './pages/PlayPage'
import EventsPage from './pages/EventsPage'
import MePage from './pages/MePage'
import LeaderboardPage from './pages/LeaderboardPage'
import ScorecardPage from './pages/ScorecardPage'
import GroupsPage from './pages/GroupsPage'
import HistoryPage from './pages/HistoryPage'
import CommissionerPage from './pages/CommissionerPage'
import PlayerLoginPage from './pages/PlayerLoginPage'
import SignupPage from './pages/SignupPage'
import ScheduleRoundPage from './pages/ScheduleRoundPage'
import RoundLeaderboardPage from './pages/RoundLeaderboardPage'
import ProfileEditPage from './pages/ProfileEditPage'
import EventLandingPage from './pages/EventLandingPage'
import EventCreatePage from './pages/EventCreatePage'
import CoursesPage from './pages/CoursesPage'
import EditRoundPage from './pages/EditRoundPage'
import ActivityPage from './pages/ActivityPage'
import TermsPage from './pages/TermsPage'
import PrivacyPage from './pages/PrivacyPage'
import ShufflePage from './pages/ShufflePage'
import GamePage from './pages/GamePage'
// Lazy — pulls Mapbox; only loaded when the admin opens the mapper.
const AdminCourseMapPage = lazy(() => import('./pages/AdminCourseMapPage'))

// Pre-app flow: splash → install → auth gate → app
//
// Auth gate (after install is dismissed):
//   - session + completed player → app routes
//   - session, no player or incomplete → SignupPage
//   - no session → PlayerLoginPage (phone OTP)
//
// Commissioner access is no longer a separate PIN-gated track. Admin surfaces
// (event setup, league setup) are reachable by any signed-in player; the real
// role enforcement lives server-side at event-creation time.

export default function App() {
  const { loading, playerLoading, session, isSignedUp, player, playerMissingConfirmed } = useAuth()

  // Skip splash + install when there's a fresh active round in localStorage —
  // getting the user back to scoring is more important than the marketing
  // splash and the install prompt. The auth gate still runs (stage='app'
  // triggers session + player load below), so we don't punch a hole in
  // authentication. 24h freshness window matches the app's mental model of
  // "round in progress right now"; older rounds go through normal boot.
  // Live evidence 2026-07-11: on a bad round, every phone-lock cycle forced
  // the user back through splash + install → login → home → resume-tile →
  // scorecard. Four hops before scoring resumes; this drops it to one.
  const [stage, setStage] = useState(() => {
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('active_round') : null
      if (raw) {
        const parsed = JSON.parse(raw)
        const updated = Number(parsed?.updated_at) || 0
        if (updated > Date.now() - 24 * 60 * 60 * 1000) return 'app'
      }
    } catch {}
    return 'splash'
  })

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault()
      window._deferredInstallPrompt = e
    }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  // PUBLIC ROUTES — short-circuit before any splash/install/auth logic so
  // /terms and /privacy render for anyone, including Twilio A2P 10DLC
  // campaign reviewers who hit them anonymously. Without this, react-
  // router's catch-all falls through to PlayerLoginPage and Twilio flags
  // the campaign for "policy not accessible."
  //
  // Using window.location directly here (rather than useLocation()) so
  // we don't have to plumb router state through the splash/install
  // stages — those return before any Routes render.
  const path = typeof window !== 'undefined' ? window.location.pathname : ''
  if (path === '/terms' || path === '/terms/') return <TermsPage />
  if (path === '/privacy' || path === '/privacy/') return <PrivacyPage />

  // Splash
  if (stage === 'splash') {
    return (
      <SplashScreen
        ready={!loading}
        onDone={() => setStage('install')}
      />
    )
  }

  // Install prompt (self-skips if not needed)
  if (stage === 'install') {
    return (
      <InstallPrompt
        visible={true}
        onDone={() => setStage('app')}
      />
    )
  }

  // Stage = 'app' — apply auth gate.
  // GATE 1 (boot splash): show the splash ONLY on a genuine first cold start
  // with nothing to paint — auth still resolving (initial `loading` OR a
  // background→foreground resume re-fetch `playerLoading`) AND no player
  // available. The moment an instant-paint cached / last-good `player` is on
  // screen AND the session has resolved, we keep rendering the app through the
  // background re-fetch (SWR), so a short OR long background never flashes
  // real-screen → splash → real-screen. The resume re-fetch keeps the
  // last-good player (stale-while-revalidate in AuthContext), so a transient
  // null fetch never reaches this gate as a spurious logout.
  //
  // The gate ALSO holds the neutral splash while the initial mount is still
  // `loading` and `session` has not yet resolved — even with a painted
  // player — because `isAuthed` below is (session && isSignedUp) and would be
  // false with `session` still null, dropping the user onto PlayerLoginPage /
  // SignupPage for a frame on the active-round fast-path cold boot. Holding
  // until getSession() resolves closes that login-flash. See
  // shouldShowBootSplash.
  if (shouldShowBootSplash({ loading, playerLoading, session, player })) {
    return <SplashScreen ready={false} onDone={() => {}} />
  }

  const isAuthed = session && isSignedUp

  // SignupPage ("Finish your profile") renders ONLY when we are SURE the
  // profile is missing or incomplete: a live session plus EITHER a player row
  // that exists but never completed signup (tos_accepted_at NULL → isSignedUp
  // false), OR the server authoritatively reporting no row at all
  // (playerMissingConfirmed — a brand-new OTP user or a deleted row).
  //
  // A transient fetch failure (cold-started function, network drop, 401 in
  // token rotation) is NOT proof of a missing profile, so when the session is
  // live but we could not confirm the player either way, we hold on the splash
  // rather than falsely demanding re-signup. On a killed-PWA phone cold start
  // this is the difference between "still logged in" and the reported logout.
  if (session && !isSignedUp) {
    if (shouldShowSignup({ session, isSignedUp, player, playerMissingConfirmed })) {
      return <SignupPage />
    }
    return <SplashScreen ready={false} onDone={() => {}} />
  }

  if (!isAuthed) {
    return (
      <Routes>
        <Route path="*" element={<PlayerLoginPage />} />
      </Routes>
    )
  }

  // RESTORE GATE (Apple 5.1.1(v)): a signed-in user whose own player row carries
  // a pending deletion_requested_at must NOT proceed into the app. Block entry
  // and show the restore modal — Yes revokes the deletion and continues, No
  // signs out. This sits AFTER isAuthed (the row exists + is signed up; a soft
  // delete never clears tos_accepted_at) and BEFORE any app route renders.
  if (shouldShowRestoreGate({ session, player })) {
    return <RestoreAccountGate />
  }

  return (
    <>
      <ScrollToTop />
      <ColdOpenToHome />
      <ApplyPendingUpdateOnNav />
      <ResumeToActiveRound />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/play" element={<PlayPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/me" element={<MePage />} />
        <Route path="/leaderboard" element={<LeaderboardPage />} />
        <Route path="/scorecard" element={<ScorecardPage />} />
        <Route path="/groups" element={<GroupsPage />} />
        <Route path="/history" element={<HistoryPage />} />
        <Route path="/courses" element={<CoursesPage />} />
        <Route path="/schedule" element={<ScheduleRoundPage />} />
        <Route path="/round-leaderboard" element={<RoundLeaderboardPage />} />
        <Route path="/shuffle" element={<ShufflePage />} />
        <Route path="/game/:gameId" element={<GamePage />} />
        <Route path="/event-setup" element={<CommissionerPage />} />
        {/* T2 (App Store 2.1): /league-setup was a dead "Coming soon" stub that
            was still reachable — an app-review placeholder risk. Real league
            admin lives in EventDraftAdmin via /event-setup (CommissionerPage),
            so the stub route now redirects home rather than showing an empty
            "Coming soon" page. */}
        <Route path="/league-setup" element={<Navigate to="/" replace />} />
        <Route path="/profile-edit" element={<ProfileEditPage />} />
        {/* T2: Pro upgrade page removed for the free launch (no IAP yet).
            Any lingering link to /account/pro redirects home rather than
            steering to web payment (App Store 3.1.1). */}
        <Route path="/account/pro" element={<Navigate to="/" replace />} />
        <Route path="/event/:eventId" element={<EventLandingPage />} />
        <Route path="/events/new" element={<EventCreatePage />} />
        <Route path="/edit-round/:round_score_id" element={<EditRoundPage />} />
        <Route path="/activity" element={<ActivityPage />} />
        {/* T6: /admin/course-map is a pure-admin surface (the Mapbox course
            mapper). Gate it client-side so a signed-in NON-admin who types the
            URL is bounced Home instead of seeing the editor. Server-side role
            enforcement still applies to any writes; this closes the client-side
            UI leak. /event-setup stays open (a user feature). */}
        <Route path="/admin/course-map/:course_id" element={
          <RequireAdmin>
            <Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>Loading map…</div>}>
              <AdminCourseMapPage />
            </Suspense>
          </RequireAdmin>
        } />
        <Route path="/commissioner" element={<Navigate to="/event-setup" replace />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/player-login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <BottomNav />
      {player && <NotificationSoftAsk />}
    </>
  )
}

// RequireAdmin — client-side gate for pure-admin routes (T6). A signed-in
// non-admin who navigates to a wrapped route (e.g. /admin/course-map) is
// redirected Home instead of rendering the admin UI. Reached only after the
// App-level auth gate, so `player` is resolved here; canAccessAdminRoutes
// denies a null player and any non-admin role. Server-side enforcement is
// unchanged — this only removes the client-side UI leak.
function RequireAdmin({ children }) {
  const { player } = useAuth()
  if (!canAccessAdminRoutes(player)) return <Navigate to="/" replace />
  return children
}

// RestoreAccountGate — the sign-in side of in-app account deletion (Apple
// 5.1.1(v)). Rendered by the App gate when the signed-in user's row has a
// pending deletion_requested_at. Full-screen blocking modal (no app chrome
// behind it): "Yes, restore" revokes the deletion (restore-account clears the
// flag, refreshPlayer drops it from state so the gate re-opens the app); "No,
// sign out" leaves the deletion pending and signs the user back out.
function RestoreAccountGate() {
  const { restoreAccount, signOutAccount } = useAuth()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const onRestore = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    const res = await restoreAccount()
    if (res?.error) {
      setError('Could not restore your account. Please try again.')
      setBusy(false)
    }
    // On success refreshPlayer clears deletion_requested_at from state, the
    // gate stops matching, and the app renders — no local navigation needed.
  }

  const onSignOut = async () => {
    if (busy) return
    setBusy(true)
    await signOutAccount()
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'var(--green-deep, #0f2419)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      <div className="card" style={{ maxWidth: 380, width: '100%' }}>
        <h2 style={{ marginTop: 0, marginBottom: 8 }}>Restore your account?</h2>
        <p className="text-muted text-sm" style={{ marginBottom: 20 }}>
          You previously requested to delete your account. Would you like to
          revoke the request and restore your profile?
        </p>
        {error && (
          <p className="text-sm" style={{ color: 'var(--danger, #c0392b)', marginBottom: 12 }}>
            {error}
          </p>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button className="btn btn-primary btn-full" onClick={onRestore} disabled={busy}>
            {busy ? 'Please wait…' : 'Yes, restore my account'}
          </button>
          <button className="btn btn-ghost btn-full" onClick={onSignOut} disabled={busy}>
            No, sign out
          </button>
        </div>
      </div>
    </div>
  )
}

// F-074: apply a waiting/deferred service-worker update on in-app navigation to
// a SAFE (non-round) surface. A new build's SW is kept waiting while the user is
// mid-round on /scorecard or /round-leaderboard (so build-A's precache stays
// intact); the moment they navigate to a safe path (Home, Me, etc.) this applies
// it. Foreground and cold launch are handled in swController; this closes the
// in-session-navigation gap so the user doesn't stay on an old build until the
// next background/cold-launch cycle.
function ApplyPendingUpdateOnNav() {
  const location = useLocation()
  useEffect(() => {
    applyPendingUpdateIfSafe()
  }, [location.pathname])
  return null
}

// Cold-open to Home: when the PWA is re-foregrounded after a long inactive gap
// and the user wasn't mid-round, route to Home instead of dropping them on the
// last-used page. Active scorecard / round-leaderboard surfaces are skipped so
// a mid-round backgrounding doesn't get yanked away from the game.
//
// Persists the hidden-at timestamp to localStorage so a TRUE cold start (PWA
// killed by the OS) still triggers the redirect — an in-memory ref alone
// wouldn't, because the JS context is gone by the time the user re-opens.
const COLD_OPEN_THRESHOLD_MS = 30 * 60 * 1000
const HIDDEN_AT_KEY = 'tf:lastHiddenAt'
function ColdOpenToHome() {
  const navigate = useNavigate()
  const location = useLocation()

  const maybeRedirect = (hiddenAt) => {
    try { localStorage.removeItem(HIDDEN_AT_KEY) } catch {}
    if (!hiddenAt) return
    const gap = Date.now() - hiddenAt
    if (gap < COLD_OPEN_THRESHOLD_MS) return
    const path = location.pathname
    if (path === '/scorecard' || path === '/round-leaderboard' || path === '/') return
    navigate('/', { replace: true })
  }

  // Cold-start path: on mount, if a stale hidden timestamp survived the kill,
  // honor it. This is the path that matters most — backgrounding → OS kills
  // the PWA → user re-opens minutes/hours later → new JS context with no ref.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(HIDDEN_AT_KEY)
      const ts = raw ? parseInt(raw, 10) : null
      if (Number.isFinite(ts)) maybeRedirect(ts)
    } catch {}
    // intentionally on-mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Warm-resume path: visibilitychange fires while the JS context is still
  // alive (PWA backgrounded but not killed). Stamp on hidden, redirect on
  // visible if the gap exceeded the threshold.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        try { localStorage.setItem(HIDDEN_AT_KEY, String(Date.now())) } catch {}
        return
      }
      let ts = null
      try {
        const raw = localStorage.getItem(HIDDEN_AT_KEY)
        ts = raw ? parseInt(raw, 10) : null
      } catch {}
      maybeRedirect(Number.isFinite(ts) ? ts : null)
    }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [navigate, location.pathname])

  return null
}

// Cold-open resume: on a fresh authed mount, if an active round exists in
// localStorage AND the user landed on Home (the default cold-start path),
// jump straight to the scorecard so they don't have to see the splash →
// Home → tap-Resume chain to get back into their round.
//
// Session-scoped consume flag stops the redirect from firing again if
// the user manually navigates BACK to Home during the session. That way
// they can escape a round they don't want to be in.
const RESUME_CONSUMED_KEY = 'tf_resume_consumed'
function ResumeToActiveRound() {
  const navigate = useNavigate()
  const location = useLocation()
  useEffect(() => {
    if (location.pathname !== '/') return
    try {
      if (sessionStorage.getItem(RESUME_CONSUMED_KEY) === '1') return
      const raw = localStorage.getItem('active_round')
      if (!raw) return
      const ar = JSON.parse(raw)
      if (!ar?.id) return
      sessionStorage.setItem(RESUME_CONSUMED_KEY, '1')
      navigate('/scorecard', { replace: true, state: { ...ar, standalone: true } })
    } catch {}
    // intentionally on-mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return null
}
